import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SmartScreenOutlinedIcon from "@mui/icons-material/SmartScreenOutlined";
import { getControlAccess, setControlAccess } from "../api/cleaning";

// "Control Access" tab of the Controls Setup page: which control users (location
// tablets) show this device on their device controls page. Only control users based
// at the device's location are listed. A device that is switched off for a control
// user is hidden from its device list and its commands are refused by the API.
export default function ControlAccessEditor({ deviceId, showSnackbar }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingUserId, setSavingUserId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await getControlAccess(deviceId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (controlUser) => {
    const nextEnabled = !controlUser.enabled;
    const updateUser = (enabled) =>
      setData((prev) => ({
        ...prev,
        control_users: prev.control_users.map((u) =>
          u.user_pk_id === controlUser.user_pk_id
            ? {
                ...u,
                enabled,
                enabled_device_count:
                  u.enabled_device_count + (enabled === u.enabled ? 0 : enabled ? 1 : -1),
              }
            : u,
        ),
      }));

    setSavingUserId(controlUser.user_pk_id);
    updateUser(nextEnabled);
    try {
      await setControlAccess(deviceId, controlUser.user_pk_id, nextEnabled);
      showSnackbar?.(
        `${data?.device?.name || deviceId} is now ${nextEnabled ? "shown to" : "hidden from"} ${controlUser.username}`,
        "success",
      );
    } catch (err) {
      updateUser(!nextEnabled);
      showSnackbar?.(err.message, "error");
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  const controlUsers = data?.control_users || [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
        <Typography variant="h6" sx={{ flex: 1 }}>
          Show on device controls
        </Typography>
        <Tooltip title="Reload">
          <IconButton size="small" onClick={load}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose which control users can see and operate this device
        {data?.location ? (
          <>
            {" "}at <strong>{data.location.location_name}</strong>
          </>
        ) : null}
        . A device that is switched off is hidden from their device list and
        cannot be controlled.
      </Typography>

      {Number(data?.device?.active) !== 1 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This device is inactive, so it is not shown on the device controls page
          even when it is switched on here.
        </Alert>
      )}

      {!data?.location ? (
        <Alert severity="info">
          This device is not assigned to a location. Assign it to a room on the
          device page first.
        </Alert>
      ) : controlUsers.length === 0 ? (
        <Alert severity="info">
          There are no control users based at {data.location.location_name}.
        </Alert>
      ) : (
        <List disablePadding>
          {controlUsers.map((controlUser, index) => (
            <Box key={controlUser.user_pk_id}>
              {index > 0 && <Divider component="li" />}
              <ListItem
                sx={{ py: 1.5 }}
                secondaryAction={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {savingUserId === controlUser.user_pk_id && (
                      <CircularProgress size={18} />
                    )}
                    <Switch
                      edge="end"
                      checked={controlUser.enabled}
                      disabled={savingUserId === controlUser.user_pk_id}
                      onChange={() => handleToggle(controlUser)}
                      inputProps={{
                        "aria-label": `Show this device to ${controlUser.username}`,
                      }}
                    />
                  </Box>
                }
              >
                <ListItemIcon>
                  <SmartScreenOutlinedIcon
                    color={controlUser.enabled ? "primary" : "disabled"}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                    >
                      <Typography component="span" sx={{ fontWeight: 600 }}>
                        {controlUser.username}
                      </Typography>
                      {!controlUser.active && (
                        <Chip label="Inactive user" size="small" />
                      )}
                    </Box>
                  }
                  secondary={
                    <>
                      {controlUser.email}
                      {" · "}
                      {controlUser.enabled_device_count === 1
                        ? "1 device shown"
                        : `${controlUser.enabled_device_count} devices shown`}
                    </>
                  }
                />
              </ListItem>
            </Box>
          ))}
        </List>
      )}
    </Paper>
  );
}
